import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const guideArticles = articles.slice(0, 5);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-900 hover:text-primary-500 transition-colors cursor-pointer flex-shrink-0"
          >
            <span className="font-heading text-xl md:text-2xl">Sugargoo Sheet</span>
          </Link>

          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/"
              className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              Home
            </Link>

            <div ref={guideRef} className="relative">
              <button
                onClick={() => setGuideOpen(!guideOpen)}
                className="flex items-center gap-1 text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${guideOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {guideOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-background-50 border border-background-200/80 rounded-xl shadow-lg py-2 z-50">
                  {guideArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      onClick={() => setGuideOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      {article.title.length > 45 ? article.title.slice(0, 45) + '...' : article.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              Sugargoo Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <div className="md:hidden flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-1.5 bg-primary-500 text-background-50 rounded-full text-xs font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Buy Now
            </a>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-background-50 overflow-y-auto">
            <div className="px-4 py-4 space-y-6">
              <div>
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Navigation</h4>
                <div className="space-y-1">
                  <Link
                    to="/"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-home-4-line text-lg"></i>
                    Home
                  </Link>
                  <Link
                    to={`/article/${articles[0].slug}`}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-book-open-line text-lg"></i>
                    Complete Guide
                  </Link>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Shopping Guides</h4>
                <div className="space-y-1">
                  {guideArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line text-foreground-400"></i>
                      {article.title.length > 38 ? article.title.slice(0, 38) + '...' : article.title}
                    </Link>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Categories</h4>
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className={`${cat.icon} text-sm text-primary-500`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-background-200 flex flex-col gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full px-5 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors text-center whitespace-nowrap"
                >
                  Sugargoo Spreadsheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full px-5 py-3 border border-primary-300 text-primary-600 rounded-full text-sm font-semibold hover:bg-primary-50 transition-colors text-center whitespace-nowrap"
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}